import { apiRequest } from './queryClient';

interface ErrorReportOptions {
  errorMessage: string;
  errorCode?: string;
  stackTrace?: string;
  componentStack?: string;
  source: 'api_error' | 'toast_button' | 'error_boundary' | 'manual';
  url?: string;
  additionalContext?: Record<string, unknown>;
}

/**
 * Report an error to the technical difficulties tracking system
 * Used for automatic error reporting (error boundary, API errors)
 *
 * @param options - Error details and context
 * @returns Promise that resolves when report is submitted (or fails silently)
 */
// Endpoints whose errors are operational/expected, not bugs worth reporting
const SKIP_REPORT_URL_PATTERNS = [
  '/api/health/',        // Health check / monitoring endpoints
  '/api/tech-reports',   // Prevent self-referential reporting loop
  '/api/auth/verify',    // Verification token expiry is expected
];

export async function reportErrorToTech(options: ErrorReportOptions): Promise<void> {
  try {
    // Check if user is authenticated (optional - only report for logged-in users)
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return;
    }

    // Skip reporting for operational/monitoring endpoints (not bugs)
    const errorUrl = options.url || window.location.href;
    if (SKIP_REPORT_URL_PATTERNS.some(pattern => errorUrl.includes(pattern))) {
      return;
    }

    // Gather context automatically
    const context = {
      ...options.additionalContext,
      url: options.url || window.location.href,
      timestamp: new Date().toISOString(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
    };

    await apiRequest('POST', '/api/tech-reports', {
      errorMessage: options.errorMessage,
      errorCode: options.errorCode || null,
      stackTrace: options.stackTrace || null,
      componentStack: options.componentStack || null,
      source: options.source,
      url: context.url,
      additionalContext: context,
    });
  } catch (error) {
    // Fail silently - don't break app if reporting fails
    // (e.g., network error, API down, user not authenticated)

    console.error('[ErrorReporter] Failed to report error:', error);
  }
}

/**
 * Determine if an API error should be auto-reported
 * Only report server errors (5xx), not client errors (4xx)
 *
 * @param statusCode - HTTP status code
 * @returns true if error should be auto-reported
 */
export function shouldAutoReportApiError(statusCode: number): boolean {
  return statusCode >= 500;
}
